import { Dialog } from "@headlessui/react"
import { ChangeEvent, FC, useRef, useState } from "react"
import * as React from "react"
import { KButton, KSpan, KTitleSpan } from "kahuna-base-react-components"
import { lang } from "../../../../../constants/languages"
import { users } from "../../../../../apis"
import { toast } from "react-toastify"
import { errorFilledToastOptions, infoFilledToastOptions, successFilledToastOptions } from "../../../../../constants"

type ImageUploadModalProps = {
  open: boolean
  labelName: string
  onClose: () => void
  onUploadSuccess: () => void
  onSkipBeatport: () => void
}

const ImageUploadModal: FC<ImageUploadModalProps> = ({ open, labelName, onClose, onUploadSuccess, onSkipBeatport }) => {
  const [uploading, setUploading] = useState(false)
  const [onHover, setOnHover] = useState(false)
  const [skipBeatport, setSkipBeatport] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleBrowseClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  const onUploadLabelBeatportLogo = (
    skipBeatport: boolean,
    successText: string,
    errorText: string,
    logo?: FormData
  ) => {
    const skipParam = skipBeatport ? `&skip_beatport=1` : ""
    users(`/upload_label_beatport_logo/?label=${labelName}${skipParam}`, "post", logo)
      .then((resp) => {
        if (resp.data.success) {
          toast.success(successText, successFilledToastOptions)
          onUploadSuccess()
        } else {
          toast.error(resp.data.message, errorFilledToastOptions)
        }
        setUploading(false)
        toast.dismiss("LOADING_LOGO")
        if (skipBeatport) onSkipBeatport()
      })
      .catch(() => {
        toast.error(errorText, errorFilledToastOptions)
        setUploading(false)
        toast.dismiss("LOADING_LOGO")
        if (skipBeatport) onSkipBeatport()
      })
  }

  const onSkipBeatportClick = () => {
    onUploadLabelBeatportLogo(
      true,
      lang.account.label_beatport_logo.success_skip_beatport,
      lang.account.label_beatport_logo.error_skip_beatport,
      undefined
    )
  }

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const fileInput = event.target

    if (fileInput.files && fileInput.files.length > 0) {
      const file = fileInput.files[0]
      if (file.type !== "image/png" && file.type !== "image/jpeg" && file.type !== "image/jpg") {
        toast.error(lang.account.profile.error.unsupported_file_format, errorFilledToastOptions)
        return
      }

      const img = new Image()
      img.onload = () => {
        URL.revokeObjectURL(img.src)

        if (img.width !== img.height) {
          toast.error(lang.account.label_beatport_logo.error_not_square, errorFilledToastOptions)
          return
        }

        const logo = new FormData()
        logo.append("logo", file)

        setUploading(true)
        toast.loading(lang.account.label_beatport_logo.uploading_logo, {
          toastId: "LOADING_LOGO",
          ...infoFilledToastOptions
        })

        onUploadLabelBeatportLogo(
          false,
          lang.account.label_beatport_logo.success_upload_logo,
          lang.account.label_beatport_logo.error_upload_logo,
          logo
        )
      }
      img.src = URL.createObjectURL(file)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-[rgba(0,0,0,0.3)]" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <div className="mt-10 bg-white elev-b-sm rounded-lg overflow-hidden w-[650px]">
          <div
            className={`bg-gray-50 text-xs text-gray-500 font-medium
              uppercase border-b flex justify-center p-4`}
          >
            <KTitleSpan
              text={lang.catalog.asset_registry.general_information.error_text.beatport_logo_required_title}
              fontSize={24}
            />
          </div>
          <div className="w-full p-4 text-center">
            <KSpan
              text={lang.catalog.asset_registry.general_information.error_text.beatport_logo_required_desc.replace(
                "<label>",
                labelName
              )}
            />
          </div>
          <div className="px-8 pb-4">
            <div
              className="flex flex-col items-center justify-center gap-5 p-8 rounded-[10px] w-full"
              style={{
                backgroundColor: onHover ? "#F3F3F3" : "#F7F7F7"
              }}
              onMouseEnter={() => setOnHover(true)}
              onMouseLeave={() => setOnHover(false)}
            >
              <div className="flex justify-center" style={{ height: 40 }}>
                <img
                  src={onHover ? "/catalog_icons/upload-hover.svg" : "/catalog_icons/upload.svg"}
                  alt="upload-icon"
                />
              </div>
              <div className="flex flex-col gap-1 justify-center">
                <div className="justify-center flex">
                  <KSpan
                    text={lang.catalog.asset_registry.general_information.error_text.beatport_logo_upload_file_title}
                    color="#111"
                    fontWeight={500}
                  />
                </div>
                <KSpan
                  text={lang.catalog.asset_registry.general_information.error_text.beatport_logo_upload_file_desc}
                  fontSize={12}
                  lineHeight="16px"
                />
              </div>
              <div style={{ pointerEvents: "auto" }}>
                <KButton
                  text={lang.catalog.asset_registry.general_information.error_text.beatport_logo_upload_file_button}
                  onClick={handleBrowseClick}
                  height="32px"
                  padding="6px 10px"
                  background="#FFF"
                  textColor="#111"
                  disabled={uploading}
                />
              </div>
              <input
                style={{ display: "none" }}
                ref={fileInputRef}
                type="file"
                accept="image/png, image/jpeg"
                onChange={handleFileChange}
              />
            </div>
          </div>
          <div className="flex items-center gap-2 px-8 pb-4">
            <input
              type="checkbox"
              style={{ accentColor: "black", cursor: "pointer" }}
              checked={skipBeatport}
              onChange={() => setSkipBeatport(!skipBeatport)}
            />
            <KSpan text={lang.catalog.asset_registry.general_information.error_text.dont_send_to_beatport} />
          </div>
          <div className="flex gap-4 p-4 px-8">
            <KButton text={lang.common.close} background="transparent" onClick={onClose} disabled={uploading} />
            {skipBeatport && (
              <KButton
                text={lang.catalog.asset_registry.general_information.save_and_next_step_button}
                background="black"
                textColor="white"
                onClick={() => onSkipBeatportClick()}
              />
            )}
          </div>
        </div>
      </div>
    </Dialog>
  )
}

export default ImageUploadModal
